import React from 'react';
import {
  Box,
  Flex,
  Text,
  Avatar,
  IconButton,
  toast
} from '@sparrowengg/twigs-react';
import { SingleAppProps } from '../types';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CopyIcon,
  LockIcon
} from '@sparrowengg/twigs-react-icons';
import CustomPill from 'commons/components/custom-pill';
import SparklesIcon from 'commons/icons/sparkles';
import { truncateText } from 'commons/helper';
import { appTypeConstants } from 'features/apps/add-apps/constants';

const SingleApp = ({ app }: SingleAppProps) => {
  const navigate = useNavigate();

  const isDevMode =
    app?.appStatus === appTypeConstants.DRAFT ||
    app?.appType === appTypeConstants.DEVAPP;
  const isLocalDevApp = app?.appType === appTypeConstants.DEVAPP;
  const isRegularDevApp =
    app?.appStatus === appTypeConstants.DRAFT &&
    app?.appType !== appTypeConstants.DEVAPP;
  const isPublicApp = app?.appType === appTypeConstants.PUBLIC;
  const appType =
    app?.appType === appTypeConstants.DEAL
      ? appTypeConstants.PRIVATE
      : app?.appType ?? '';

  return (
    <Flex
      flexDirection="column"
      justifyContent="space-between"
      css={{
        width: 322,
        height: 284,
        border: '$borderWidths$xs solid $neutral100',
        borderRadius: '$lg'
      }}
    >
      <Box
        css={{
          padding: '$10'
        }}
      >
        <Flex justifyContent="space-between">
          <Avatar size="2xl" src={app?.icon} rounded="md" />
          <CustomPill
            label={
              appType?.charAt(0).toUpperCase() +
              appType?.slice(1)?.toLowerCase()
            }
            variant={isPublicApp ? 'accent' : 'warning'}
            radius="md"
            isClosable={false}
            icon={
              isPublicApp ? (
                <SparklesIcon />
              ) : (
                <LockIcon size={16} color="#B47409" />
              )
            }
          />
        </Flex>
        <Flex
          flexDirection="column"
          gap="$1"
          css={{
            marginTop: '$6',
            width: '100%',
            wordBreak: 'break-word',
            textWrap: 'wrap'
          }}
        >
          <Text size="md" weight="bold" css={{ whiteSpace: 'break-spaces' }}>
            {truncateText(app.name, 40)}
          </Text>
          <Text size="sm" css={{ color: '$neutral800' }}>
            {truncateText(app.shortDescription, 70)}
          </Text>
        </Flex>
        <Flex alignItems="center" gap="$4" css={{ marginTop: '$6' }}>
          <CustomPill
            label={isDevMode ? 'Dev Mode' : 'Published'}
            radius="md"
            isClosable={false}
            variant={isDevMode ? 'secondary' : 'success'}
          />
          <Text
            size="xs"
            css={{ color: '$secondary500' }}
          >{`v${app.versionNumber?.toFixed(1)}`}</Text>
          {!!app.incomingWebhookURL ? (
            <IconButton
              size="xs"
              variant="ghost"
              color="default"
              icon={<CopyIcon />}
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(app.incomingWebhookURL);
                toast({
                  variant: 'success',
                  description: 'Webhook URL been copied to the clipboard.'
                });
              }}
            />
          ) : null}
        </Flex>
      </Box>
      <Flex
        alignItems="center"
        justifyContent="center"
        gap="$2"
        onClick={() => {
          if (isRegularDevApp) {
            navigate(`/code-editor?app=${app?.appVersionId}`);
          } else if (isLocalDevApp) {
            navigate(`/edit-local-dev-app/${app?.appId}`);
          } else {
            navigate(`/edit-app/${app?.appId}`);
          }
        }}
        css={{
          padding: '$6',
          borderRadius: '0 0 $lg $lg',
          background: isDevMode ? '#F3F9FF' : '$primary50',
          color: isDevMode ? '#3E90FD' : '$primary500',
          cursor: 'pointer'
        }}
      >
        <Text size="md" weight="bold">
          {isRegularDevApp ? 'Continue building' : 'Check app'}
        </Text>
        <Box css={{ transform: 'scaleX(-1)' }}>
          <ArrowLeftIcon size={16} />
        </Box>
      </Flex>
    </Flex>
  );
};

export default SingleApp;
